import { CONFIG } from '../config';
import { DECOR } from '../content/decor';
import { INTERIOR, isFloor, isWall } from '../content/interior';
import { isRide, type RideId } from '../content/rides';
import { INTERIOR_MAX_X } from '../world/rooms';
import { angleDelta, clamp } from '../core/math';
import { DEFAULT_LOOK } from '../state/defaults';
import {
  BEARD_STYLES,
  BODY_TYPES,
  BOTTOM_STYLES,
  HAIR_STYLES,
  HAT_STYLES,
  SHOE_STYLES,
  TOP_STYLES,
  type CharacterLook,
} from '../state/types';
import type { Pose } from '../world/builders/character';
import { HOME_LOTS } from '../world/home';

/**
 * The multiplayer wire protocol. Everything a peer sends is untrusted input, so each
 * message type has a parser here that validates, clamps and sanitizes it.
 */

export const PROTOCOL_VERSION = 8;
export const APP_ID = 'double-wide-dreams';
/** Other players shown per park (you + 7). */
export const MAX_NEIGHBORS = 7;
export const STATE_INTERVAL_MS = 100;
/** Idle players still send a state this often, so others know they're around. */
export const HEARTBEAT_MS = 1000;
export const STATUS_INTERVAL_MS = 5000;
export const CHAT_MAX = 80;
export const NAME_MAX = 18;

// --- park codes ------------------------------------------------------------------------

/** No 0/O or 1/I, so a code survives being read aloud over the phone. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 6;
const CODE_PATTERN = new RegExp(`^[${CODE_ALPHABET}]{${CODE_LENGTH}}$`);

export function generateParkCode(random: () => number = Math.random): string {
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) code += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)];
  return code;
}

/** Accepts "k7q-x2m", " K7QX2M " etc. Returns the canonical code, or null if it isn't one. */
export function normalizeParkCode(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const code = input.toUpperCase().replace(/[\s-]/g, '');
  return CODE_PATTERN.test(code) ? code : null;
}

/** "K7QX2M" -> "K7Q-X2M" for display. */
export const formatParkCode = (code: string): string => `${code.slice(0, 3)}-${code.slice(3)}`;

export const roomIdFor = (code: string): string => `park-${code}`;
/** Encrypts the connection handshake, so signaling relays never see it in the clear. */
export const passwordFor = (code: string): string => `lucky-acres:${code}`;

export function inviteUrl(base: string, code: string): string {
  const url = new URL(base);
  url.search = '';
  url.hash = '';
  url.searchParams.set('park', code);
  return url.toString();
}

// --- player state (sent ~10 times a second) ----------------------------------------------

export const POSES = [
  'stand',
  'sit',
  'sitdrink',
  'rock',
  'drink',
  'carry',
  'push',
  'wave',
  'cheer',
  'fallen',
  'eat',
  'aim',
  'ride',
  'armwrestle',
  'kneel',
  'throw',
  'pull',
  'scoot',
  'scootdrink',
] as const satisfies readonly Pose[];

export interface RemoteState {
  x: number;
  y: number;
  z: number;
  facing: number;
  pose: Pose;
  speed: number;
  sprint: boolean;
  airborne: boolean;
  driving: boolean;
  /** Not visible in the world (e.g. inside the car). */
  hidden: boolean;
  /** Has a menu or their phone open. */
  menu: boolean;
  asleep: boolean;
  /** Their flashlight or headlamp is on. */
  lamp: boolean;
  /** Hip-pivot rotation for trampoline flips, wrapped to [-PI, PI]. */
  flip: number;
  /** Seat height while sitting. */
  seat: number;
  carX: number;
  carZ: number;
  carHeading: number;
  /** Sender's clock (ms), used to replay movement smoothly despite network jitter. */
  t: number;
}

const FLAGS = { sprint: 1, airborne: 2, driving: 4, hidden: 8, menu: 16, asleep: 32, lamp: 64 } as const;
const STATE_FIELDS = 13;

const r2 = (v: number): number => Math.round(v * 100) / 100;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;
/** Wraps an angle to [-PI, PI]. */
export const wrapAngle = (a: number): number => angleDelta(0, a);

/** Compact array encoding (~70 bytes of JSON per update). */
export function encodeState(s: RemoteState): number[] {
  let flags = 0;
  for (const [key, bit] of Object.entries(FLAGS)) if (s[key as keyof typeof FLAGS]) flags |= bit;
  return [
    r2(s.x),
    r2(s.y),
    r2(s.z),
    r3(wrapAngle(s.facing)),
    Math.max(0, POSES.indexOf(s.pose as (typeof POSES)[number])),
    r2(s.speed),
    flags,
    r3(wrapAngle(s.flip)),
    r2(s.seat),
    s.driving ? r2(s.carX) : 0,
    s.driving ? r2(s.carZ) : 0,
    s.driving ? r3(wrapAngle(s.carHeading)) : 0,
    Math.round(s.t),
  ];
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const { minX, maxX, minZ, maxZ } = CONFIG.bounds;

export function decodeState(raw: unknown): RemoteState | null {
  if (!Array.isArray(raw) || raw.length !== STATE_FIELDS || !raw.every(finite)) return null;
  const [x, y, z, facing, pose, speed, flags, flip, seat, carX, carZ, carHeading, t] = raw as number[];
  const f = flags!;
  return {
    // Rooms (inside trailers, the bar) are built out past the east edge of the park.
    x: clamp(x!, minX, INTERIOR_MAX_X),
    y: clamp(y!, 0, 12),
    z: clamp(z!, minZ, maxZ),
    facing: wrapAngle(facing!),
    pose: POSES[clamp(Math.floor(pose!), 0, POSES.length - 1)] ?? 'stand',
    speed: clamp(speed!, 0, 30),
    sprint: (f & FLAGS.sprint) !== 0,
    airborne: (f & FLAGS.airborne) !== 0,
    driving: (f & FLAGS.driving) !== 0,
    hidden: (f & FLAGS.hidden) !== 0,
    menu: (f & FLAGS.menu) !== 0,
    asleep: (f & FLAGS.asleep) !== 0,
    lamp: (f & FLAGS.lamp) !== 0,
    flip: wrapAngle(flip!),
    seat: clamp(seat!, 0, 1.5),
    carX: clamp(carX!, minX, maxX),
    carZ: clamp(carZ!, minZ, maxZ),
    carHeading: wrapAngle(carHeading!),
    t: Math.max(0, t!),
  };
}

// --- profile ---------------------------------------------------------------------------

export interface HelloMsg {
  v: number;
  name: string;
  look: CharacterLook;
  /** The home lot they claim (index into HOME_LOTS). */
  lot: number;
  /** They opened this park (and get first dibs on Lot 7). */
  host: boolean;
  /** When they joined the park (ms since epoch); earlier arrivals keep contested lots. */
  since: number;
}

/** A valid home lot index, or 0 (Lot 7). */
export function parseLot(raw: unknown): number {
  return finite(raw) && Number.isInteger(raw) && raw >= 0 && raw < HOME_LOTS.length ? raw : 0;
}

const COLOR = /^#[0-9a-fA-F]{6}$/;

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(v as T) ? (v as T) : fallback;
const color = (v: unknown, fallback: string): string =>
  typeof v === 'string' && COLOR.test(v) ? v : fallback;

/**
 * Control characters and invisible formatting characters (zero-width spaces, text
 * direction overrides used to spoof text), except the joiner that glues emoji together.
 */
const INVISIBLE = /(?!\u{200D})[\p{Cc}\p{Cf}]/gu;

/** Collapses whitespace, strips invisible characters and trims to `max` characters. */
export function cleanText(raw: string, max: number): string {
  return [...raw.replace(/\s+/g, ' ').replace(INVISIBLE, '').trim()].slice(0, max).join('').trim();
}

export function sanitizeName(raw: unknown): string {
  return (typeof raw === 'string' && cleanText(raw, NAME_MAX)) || 'Neighbor';
}

/** Never trust a peer's look: every field is validated and unknown values fall back to defaults. */
export function sanitizeLook(raw: unknown): CharacterLook {
  const l = isObj(raw) ? raw : {};
  const d = DEFAULT_LOOK;
  return {
    body: oneOf(l.body, BODY_TYPES, 'man'),
    skin: color(l.skin, d.skin),
    hair: oneOf(l.hair, HAIR_STYLES, d.hair),
    hairColor: color(l.hairColor, d.hairColor),
    beard: oneOf(l.beard, BEARD_STYLES, d.beard),
    top: oneOf(l.top, TOP_STYLES, d.top),
    topColor: color(l.topColor, d.topColor),
    bottom: oneOf(l.bottom, BOTTOM_STYLES, d.bottom),
    bottomColor: color(l.bottomColor, d.bottomColor),
    shoes: oneOf(l.shoes, SHOE_STYLES, d.shoes),
    shoeColor: color(l.shoeColor, d.shoeColor),
    hat: oneOf(l.hat, HAT_STYLES, d.hat),
    hatColor: color(l.hatColor, d.hatColor),
    belly: clamp(finite(l.belly) ? l.belly : d.belly, 0, 1),
    height: clamp(finite(l.height) ? l.height : d.height, 0.85, 1.12),
    shades: l.shades === true,
  };
}

export type HelloResult = HelloMsg | 'version' | null;

/** A valid hello, 'version' if the peer runs an incompatible build, or null for junk. */
export function parseHello(raw: unknown): HelloResult {
  if (!isObj(raw)) return null;
  if (raw.v !== PROTOCOL_VERSION) return finite(raw.v) ? 'version' : null;
  return {
    v: PROTOCOL_VERSION,
    name: sanitizeName(raw.name),
    look: sanitizeLook(raw.look),
    lot: parseLot(raw.lot),
    host: raw.host === true,
    since: finite(raw.since) ? clamp(raw.since, 0, 1e15) : Date.now(),
  };
}

// --- status, chat, social, effects -------------------------------------------------------

/** 0 = Camaro on blocks, 1 = she runs, 2 = sold. */
export type CarState = 0 | 1 | 2;

export interface StatusMsg {
  rating: number;
  rank: string;
  day: number;
  lot: number;
  /** Yard decor they own (so their lot looks like theirs). */
  decor: string[];
  car: CarState;
  /** 0 = single-wide, 1 = they bought the double wide. */
  home: 0 | 1;
  /** What's on display inside, and the paint and carpet. */
  inside: string[];
  walls: string;
  floor: string;
  /** Dollars they've put into buying the park back from Apex. */
  fund: number;
  /** What's in their driveway. */
  ride: RideId;
  /** Personal bests for the park records board. */
  rec: Records;
  /** How they look right now (new clothes, a new haircut since they said hello). */
  look?: CharacterLook;
}

/** Park records: bull ride (tenths of a second), mud run (tenths, lower is better), cans, catfish lbs, duel wins. */
export interface Records {
  bull: number;
  mud: number;
  shoot: number;
  catfish: number;
  duels: number;
}

export const NO_RECORDS: Records = { bull: 0, mud: 0, shoot: 0, catfish: 0, duels: 0 };

export function parseRecords(raw: unknown): Records {
  if (!isObj(raw)) return { ...NO_RECORDS };
  const n = (v: unknown, max: number): number => (finite(v) ? clamp(Math.round(v), 0, max) : 0);
  return {
    bull: n(raw.bull, 80),
    mud: n(raw.mud, 6000),
    shoot: n(raw.shoot, 60),
    catfish: n(raw.catfish, 80),
    duels: n(raw.duels, 1e5),
  };
}

const INTERIOR_IDS = new Set(INTERIOR.map((d) => d.id));

/** Only known interior item ids, each once. */
export function sanitizeInterior(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((d): d is string => typeof d === 'string' && INTERIOR_IDS.has(d)))];
}

const DECOR_IDS = new Set(DECOR.map((d) => d.id));

/** Only known decor ids, each once. */
export function sanitizeDecor(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((d): d is string => typeof d === 'string' && DECOR_IDS.has(d)))];
}

export function parseStatus(raw: unknown): StatusMsg | null {
  if (!isObj(raw) || !finite(raw.rating) || !finite(raw.day) || typeof raw.rank !== 'string') return null;
  return {
    rating: clamp(Math.round(raw.rating), 0, 1e6),
    rank: cleanText(raw.rank, 32),
    day: clamp(Math.round(raw.day), 1, 1e5),
    lot: parseLot(raw.lot),
    decor: sanitizeDecor(raw.decor),
    car: raw.car === 1 || raw.car === 2 ? raw.car : 0,
    home: raw.home === 1 ? 1 : 0,
    inside: sanitizeInterior(raw.inside),
    walls: typeof raw.walls === 'string' && isWall(raw.walls) ? raw.walls : 'paneling',
    floor: typeof raw.floor === 'string' && isFloor(raw.floor) ? raw.floor : 'shag',
    fund: finite(raw.fund) ? clamp(Math.round(raw.fund), 0, 1e7) : 0,
    ride: isRide(raw.ride) ? raw.ride : 'camaro',
    rec: parseRecords(raw.rec),
    ...(isObj(raw.look) ? { look: sanitizeLook(raw.look) } : {}),
  };
}

export function sanitizeChat(raw: unknown): string | null {
  return (typeof raw === 'string' && cleanText(raw, CHAT_MAX)) || null;
}

export const SOCIAL_KINDS = ['clink', 'gift', 'dap'] as const;
export type SocialKind = (typeof SOCIAL_KINDS)[number];

export function parseSocial(raw: unknown): SocialKind | null {
  return isObj(raw) && SOCIAL_KINDS.includes(raw.kind as SocialKind) ? (raw.kind as SocialKind) : null;
}

export type FxMsg =
  | { kind: 'fireworks'; x: number; z: number }
  | { kind: 'tornado' }
  | { kind: 'cops' }
  | { kind: 'stunt'; ok: boolean; name: string }
  | { kind: 'ufo'; x: number; z: number };

export function parseFx(raw: unknown): FxMsg | null {
  if (!isObj(raw)) return null;
  switch (raw.kind) {
    case 'fireworks':
      if (!finite(raw.x) || !finite(raw.z)) return null;
      return { kind: 'fireworks', x: clamp(raw.x, minX, maxX), z: clamp(raw.z, minZ, maxZ) };
    case 'ufo':
      if (!finite(raw.x) || !finite(raw.z)) return null;
      return { kind: 'ufo', x: clamp(raw.x, minX, maxX), z: clamp(raw.z, minZ, maxZ) };
    case 'tornado':
    case 'cops':
      return { kind: raw.kind };
    case 'stunt':
      return {
        kind: 'stunt',
        ok: raw.ok === true,
        name: (typeof raw.name === 'string' && cleanText(raw.name, 40)) || 'a stunt',
      };
    default:
      return null;
  }
}

// --- duels: arm wrestling and dice between players ------------------------------------

export type DuelGame = 'arm' | 'dice';
export const DUEL_MAX_BET = 100;

export type DuelMsg =
  /** Dice invites carry a hash of the inviter's secret, so they can't pick it after seeing yours. */
  | { t: 'invite'; id: number; game: DuelGame; bet: number; commit: string }
  | { t: 'accept'; id: number; nonce: string }
  | { t: 'decline'; id: number; why: 'busy' | 'broke' | 'no' }
  | { t: 'cancel'; id: number }
  /** Arm wrestling: how hard you're pushing right now. */
  | { t: 'push'; id: number; v: number }
  /** Arm wrestling: where the arms are, from the inviter's side (-1 inviter loses, 1 wins). */
  | { t: 'arm'; id: number; a: number }
  | { t: 'end'; id: number; a: number }
  /** Dice: the inviter's secret, revealed. */
  | { t: 'reveal'; id: number; nonce: string };

const HEX = /^[0-9a-f]{1,64}$/;
const hex = (v: unknown): string | null => (typeof v === 'string' && HEX.test(v) ? v : null);

export function parseDuel(raw: unknown): DuelMsg | null {
  if (!isObj(raw) || !finite(raw.id) || !Number.isInteger(raw.id)) return null;
  const id = clamp(raw.id, 0, 1e9);
  switch (raw.t) {
    case 'invite': {
      const commit = hex(raw.commit);
      if ((raw.game !== 'arm' && raw.game !== 'dice') || !finite(raw.bet) || commit === null) return null;
      return { t: 'invite', id, game: raw.game, bet: clamp(Math.round(raw.bet), 0, DUEL_MAX_BET), commit };
    }
    case 'accept': {
      const nonce = hex(raw.nonce);
      return nonce === null ? null : { t: 'accept', id, nonce };
    }
    case 'decline':
      return { t: 'decline', id, why: raw.why === 'busy' || raw.why === 'broke' ? raw.why : 'no' };
    case 'cancel':
      return { t: 'cancel', id };
    case 'push':
      return finite(raw.v) ? { t: 'push', id, v: clamp(raw.v, 0, 4) } : null;
    case 'arm':
    case 'end':
      return finite(raw.a) ? { t: raw.t, id, a: clamp(raw.a, -1, 1) } : null;
    case 'reveal': {
      const nonce = hex(raw.nonce);
      return nonce === null ? null : { t: 'reveal', id, nonce };
    }
    default:
      return null;
  }
}

/** Preset lines for quick chat. */
export const QUICK_CHAT: readonly string[] = [
  'Git-R-Done! :flex:',
  'Crack a cold one? :cheers:',
  'Hold my beer. :beer:',
  'Meet me at the Gas-N-Grub :gas-pump:',
  "Rent's due, y'all :trailer:",
  'Sport Mode activated :gator:',
  "Y'all seein' this?? :tornado:",
  'Bless your heart :nails:',
  'I got a guy. :shaka:',
];

/** Spaces out events per key (anti-spam). */
export class RateLimiter {
  private readonly last = new Map<string, number>();

  /** True, and records the event, if at least `minMs` passed since the last allowed one for `key`. */
  allow(key: string, now: number, minMs: number): boolean {
    const prev = this.last.get(key);
    if (prev !== undefined && now - prev < minMs) return false;
    this.last.set(key, now);
    return true;
  }

  forget(prefix: string): void {
    for (const key of [...this.last.keys()]) if (key.startsWith(prefix)) this.last.delete(key);
  }
}
